import type { NormalizedOptions } from 'ky'
import qs from 'query-string'

import type {
  TicketUrlResponse,
  AutoInsightFeedbackRequest,
  TicketMessagesSentimentResponse as ServicesTicketMessageSentimentResponse,
  TicketMessageSentiment as ServicesTicketMessageSentiment,
  ConversationEventResponse,
} from '@zendesk/zqa-services/tickets'
import type {
  AutoQaReviewForConversationRequest,
  AutoQaReviewForConversationResponse,
} from '@zendesk/zqa-services/autoqa'
import type {
  AdvancedScorecard_AutoQaScores_Request,
  AdvancedScorecard_AutoQaScores_Response,
  AdvancedScorecard_GetWorkspaceScorecardsForReviewResponse,
  AdvancedScorecard_WorkspaceScorecard,
  AdvancedScorecard_GetWorkspaceScorecardRequest,
  AdvancedScorecard_GetReviewWorkspaceScorecardsRequest,
} from '@zendesk/zqa-services/scorecards'
import type {
  GroupedReaction,
  ReviewerQa_DeleteEvaluationCommentRequest,
  ReviewerQa_Evaluation,
  ReviewerQa_EvaluationCommentRequest,
  ReviewerQa_EvaluationCommentResponse,
  ReviewerQa_Evaluations,
  ReviewerQa_EvaluationScoreRequest,
  ReviewerQa_UpdateEvaluationCommentRequest,
  ToggleReactionResponse,
} from '@zendesk/zqa-services/reviews'
import pMemoize from 'p-memoize'
import ExpiryMap from 'expiry-map'
import type { KlausSentiment } from '@/types/sentiment'
import { oldApi, api, errorHandlerHook } from '@/api'
import { session } from '@/composables/useSession'
import type { TicketData } from '@/modules/conversations/types'
import { uploadUnsavedImages } from '@/modules/shared/CommentEditor/plugins/useUnsavedImages'
import usePromptTestingWorkspace from '@/modules/settings/composables/usePromptTestingWorkspace'

import type { Comment, CommentTag, Feedback, ReactionType } from '../types/feedback'
import type { Scorecard } from '../types/scorecard'

interface ReviewBody {
  comment: string | null
  commentTags: CommentTag[]
  conversationId: string
  messageId?: string
  transcriptionId?: string
  transcriptionMessageId?: string
  ratings: {
    categoryId: number
    rating: number
    cause?: string[]
  }[]
  revieweeId?: number
  botRevieweeId?: string
  assignmentId?: string | null
}

export interface ModifyReview {
  connectionId: number
  reviewId: string
  data?: ReviewBody
}

type FeedbackResponse = Promise<{ feedback: Feedback[] }>

const { activeWorkspace: promptTestingWorkspace } = usePromptTestingWorkspace()

const getRequestPrefix = () => `payments/${session.account.id}/teams/${session.workspace.id}`

export const getRequestHeaders = () => ({
  'X-Klaus-Workspace': (promptTestingWorkspace.value?.id ?? session.workspace.id).toString(),
})

/**
 * This sets the "viewed" state for the whole conversation - it's an indication for
 * the user if they've opened this conversation on Klaus before
 */
export const markTicketViewed = (connectionId: number, externalId: string) =>
  api.put(`ticket/connection/${connectionId}/mark-viewed`, {
    headers: getRequestHeaders(),
    searchParams: { externalId },
  })

export const toggleHighlight = ({
  conversationExternalId,
  paymentTokenId,
  messageExternalId,
}: {
  conversationExternalId: string
  paymentTokenId: number
  messageExternalId?: string
}) =>
  api.put(`ticket/connection/${paymentTokenId}/star`, {
    headers: { 'X-Klaus-Workspace': session.workspace.id.toString() },
    json: { messageExternalId, conversationExternalId },
  })

export const postReview = async (payload: { connectionId: number; data: ReviewBody }): Promise<Feedback> => {
  const response = await api
    .post(`ticket/connection/${payload.connectionId}/reviews`, { json: payload.data, headers: getRequestHeaders() })
    .json<Feedback>()

  await uploadUnsavedImages(payload.data.comment ?? undefined)

  return response
}

export const postAssignmentV2Review = async (payload: {
  connectionId: number
  assignmentId: string
  data: ReviewBody
}): Promise<Feedback> => {
  const response = await api
    .post(`ticket/connection/${payload.connectionId}/assignment/v2/${payload.assignmentId}/reviews`, {
      json: payload.data,
      headers: getRequestHeaders(),
    })
    .json<Feedback>()

  await uploadUnsavedImages(payload.data.comment ?? undefined)

  return response
}

export const updateReview = async ({ connectionId, reviewId, data }: ModifyReview): FeedbackResponse => {
  const response = await api
    .put(`ticket/connection/${connectionId}/reviews/${reviewId}`, {
      json: data,
      headers: getRequestHeaders(),
    })
    .json<FeedbackResponse>()

  await uploadUnsavedImages(data?.comment ?? undefined)

  return response
}

export const deleteReview = ({ connectionId, reviewId }: ModifyReview): FeedbackResponse =>
  api.delete(`ticket/connection/${connectionId}/reviews/${reviewId}`, { headers: getRequestHeaders() }).json()

export const setReviewAsViewed = ({ connectionId, reviewId }: ModifyReview) =>
  api.put(`ticket/connection/${connectionId}/reviews/${reviewId}/viewed`, { headers: getRequestHeaders() }).json()

export const getFeedback = (params: {
  connectionId: number | string
  conversationId: string
  language?: string
}): FeedbackResponse => {
  const query = new URLSearchParams()
  query.append('conversationId', params.conversationId)
  if (params.language) query.append('language', params.language)
  return api.get(`ticket/connection/${params.connectionId}/feedback?${query}`, { headers: getRequestHeaders() }).json()
}

export const postComment = async (payload: { connectionId: number; data: Comment }): FeedbackResponse => {
  const response = await api
    .post(`ticket/connection/${payload.connectionId}/comments`, { json: payload.data, headers: getRequestHeaders() })
    .json<FeedbackResponse>()

  await uploadUnsavedImages(payload.data.comment)

  return response
}

export const createCommentReply = async (payload: {
  connectionId: number
  id: string
  data: Comment
}): FeedbackResponse => {
  const response = await api
    .post(`ticket/connection/${payload.connectionId}/comments/${payload.id}/reply`, {
      json: payload.data,
      headers: getRequestHeaders(),
    })
    .json<FeedbackResponse>()

  await uploadUnsavedImages(payload.data.comment)

  return response
}

export const createReviewReply = async (payload: {
  connectionId: number
  id: string
  data: Comment
}): FeedbackResponse => {
  const response = await api
    .post(`ticket/connection/${payload.connectionId}/reviews/${payload.id}/reply`, {
      json: payload.data,
      headers: getRequestHeaders(),
    })
    .json<FeedbackResponse>()

  await uploadUnsavedImages(payload.data.comment)

  return response
}

export const editComment = async (payload: { connectionId: number | string; data: Comment }): FeedbackResponse => {
  const response = await api
    .put(`ticket/connection/${payload.connectionId}/comments/${payload.data.id}`, {
      json: payload.data,
      headers: getRequestHeaders(),
    })
    .json<FeedbackResponse>()

  await uploadUnsavedImages(payload.data.comment)

  return response
}

export const deleteComment = (payload: { connectionId: number; id: string }): FeedbackResponse =>
  api
    .delete(`ticket/connection/${payload.connectionId}/comments/${payload.id}`, { headers: getRequestHeaders() })
    .json()

export const setCommentAsViewed = (payload: { connectionId: number; id: string }) =>
  api
    .put(`ticket/connection/${payload.connectionId}/comments/${payload.id}/viewed`, { headers: getRequestHeaders() })
    .json()

let getTicketController = new AbortController()

export const abortOldTicketRequests = () => {
  getTicketController.abort()
  getTicketController = new AbortController()
}

export const getTicketSpanner = (externalId: string, connectionId: number): Promise<{ data: TicketData }> => {
  return api
    .extend({ hooks: { afterResponse: undefined } })
    .extend({ hooks: { afterResponse: [] } })
    .get(`ticket/connection/${connectionId}/data`, {
      headers: getRequestHeaders(),
      searchParams: { externalId },
      signal: getTicketController.signal,
    })
    .json<TicketData>()
    .then((data) => ({
      data: {
        ...data,
        paymentTokenId: Number(data.paymentTokenId),
        // TODO: Get rid of this hack and get rid of string type (in TicketMessage) when deleting this
        // Without the hack reviewing messages won't work
        externalComments: data.externalComments?.map((c) => ({
          ...c,
          id: c.externalId,
        })),
      },
    }))
}

export const getTicketBody = (externalId: string, connectionId: number) =>
  oldApi
    .extend({ hooks: { afterResponse: undefined } })
    .extend({ hooks: { afterResponse: [] } })
    .get(`${getRequestPrefix()}/tickets/connection/${connectionId}/${externalId}/body`, {
      signal: getTicketController.signal,
    })
    .json()

export const getTicketRecordings = (id: string, connectionId: string, signal?: AbortSignal) => {
  return oldApi
    .get(`${getRequestPrefix()}/tickets/v2/connection/${connectionId}/${encodeURIComponent(id)}/recording-urls`, {
      signal,
    })
    .json<{ data: Record<string, string> }>()
}

interface GetCommentRecordingParams {
  connectionId: number
  commentId: string
  ticketId: string
}
export function getCommentRecording(
  { commentId, connectionId, ticketId }: GetCommentRecordingParams,
  signal: AbortSignal,
) {
  return api
    .get(`integration-proxy/${connectionId}/external/${ticketId}/message/${commentId}/recording`, {
      headers: getRequestHeaders(),
      signal,
    })
    .blob()
}

export const getConversationEvents = (externalId: string, connectionId: number) =>
  api
    .get(`ticket/connection/${connectionId}/conversationEvents`, {
      headers: getRequestHeaders(),
      searchParams: { externalId },
      signal: getTicketController.signal,
    })
    .json<ConversationEventResponse>()

export type TicketMessageSentiment = ServicesTicketMessageSentiment & {
  sentiment: KlausSentiment
}
export type TicketMessageSentimentResponse = ServicesTicketMessageSentimentResponse & {
  messageSentiment: TicketMessageSentiment[]
}

export const getTicketSentiment = (externalId: string, connectionId: number) =>
  api
    .get(`ticket/connection/${connectionId}/messagesSentiment`, {
      headers: getRequestHeaders(),
      searchParams: { externalId },
      signal: getTicketController.signal,
    })
    .json<TicketMessageSentimentResponse>()

export const getTicketUrl = (
  { connectionId, externalId }: { connectionId: number; externalId?: string },
  signal: AbortSignal,
): Promise<TicketUrlResponse> =>
  api
    .get(`ticket/connection/${connectionId}/url`, {
      headers: getRequestHeaders(),
      searchParams: externalId ? { externalId } : {},
      signal,
    })
    .json<TicketUrlResponse>()

export const getScorecards = (externalId: string, connectionId: number | string) =>
  api
    .extend({ hooks: { afterResponse: undefined } })
    .get(`ticket/connection/${connectionId}/rating-categories`, {
      headers: getRequestHeaders(),
      searchParams: { externalId },
      hooks: {
        afterResponse: [
          (_request: Request, _options: NormalizedOptions, response: Response) => {
            if (response.status === 404) return new Response('{"scorecards": []}')
            return response
          },
          errorHandlerHook,
        ],
      },
    })
    .json<{ scorecards: Scorecard[] }>()

export const getInternalCommentTags = (): Promise<{ data: string[]; total: number }> =>
  oldApi.get(`${getRequestPrefix()}/internal-tags`).json()

export const getTranslation = (
  externalId: string,
  connectionId: number,
  language: string,
): Promise<{ body: Record<string, string | { body: string }> }> =>
  api
    .get(`ticket/connection/${connectionId}/messages/translation`, {
      headers: getRequestHeaders(),
      searchParams: { externalId, language },
    })
    .json()

export const getAutoQaConversationReviews = (
  params: AutoQaReviewForConversationRequest,
): Promise<AutoQaReviewForConversationResponse> => {
  const { connectionId, conversationId } = params
  const query = new URLSearchParams({ connectionId, conversationId })
  return api.get(`autoqa/conversation-reviews?${query}`, { headers: getRequestHeaders() }).json()
}

export const getAutoQaConversationReviewsForScorecard = (
  params: AdvancedScorecard_AutoQaScores_Request,
): Promise<AdvancedScorecard_AutoQaScores_Response> => {
  const { connectionId, externalTicketId, scorecardId } = params
  const query = new URLSearchParams({ connectionId, externalTicketId })
  return api
    .get(`settings/reviews/scorecards/${scorecardId}/autoqa/scores?${query}`, { headers: getRequestHeaders() })
    .json<AdvancedScorecard_AutoQaScores_Response>()
}

export const toggleEmojiReactions = (
  connectionId: number,
  feedbackId: string,
  type: ReactionType,
  emojis: string[],
): Promise<{ reactions: GroupedReaction[] }> => {
  return api
    .post(`ticket/connection/${connectionId}/${type}/${feedbackId}/toggle-reaction`, {
      json: { emojis },
      headers: getRequestHeaders(),
    })
    .json()
}

export const postEmojiReactionsForEvaluation = (
  connectionId: number,
  reviewId: string,
  evaluationId: string,
  emojis: string[],
): Promise<ToggleReactionResponse> => {
  return api
    .post(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}/toggle-reaction`, {
      json: { emojis },
      headers: getRequestHeaders(),
    })
    .json()
}

export const postEmojiReactionsForEvaluationComment = (
  connectionId: number,
  reviewId: string,
  evaluationId: string,
  commentId: string,
  emojis: string[],
): Promise<ToggleReactionResponse> => {
  return api
    .post(
      `ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}/comment/${commentId}/toggle-reaction`,
      {
        json: { emojis },
        headers: getRequestHeaders(),
      },
    )
    .json()
}

export const postInsightFeedback = (
  connectionId: number,
  externalTicketId: string,
  type: string,
  comment: string | undefined,
  rejected: boolean,
  customSpotlightId?: string,
): Promise<AutoInsightFeedbackRequest> =>
  api
    .post(`ticket/connection/${connectionId}/autoInsightFeedback`, {
      json: { externalTicketId, type, comment, rejected, customSpotlightId },
      headers: getRequestHeaders(),
    })
    .json()

export const updateInsightFeedback = async (
  connectionId: number,
  externalTicketId: string,
  type: string,
  comment: string | undefined,
  rejected: boolean,
  customSpotlightId?: string,
): Promise<AutoInsightFeedbackRequest> => {
  const response = await api
    .put(`ticket/connection/${connectionId}/autoInsightFeedback`, {
      json: { externalTicketId, type, comment, rejected, customSpotlightId },
      headers: getRequestHeaders(),
    })
    .json<AutoInsightFeedbackRequest>()

  await uploadUnsavedImages(comment)

  return response
}

export const deleteTicket = async (connectionId: number, externalId: string) =>
  await api.delete(`ticket/connection/${connectionId}/ticket/${externalId}`, {
    headers: getRequestHeaders(),
  })

export const getReviewEvaluation = (connectionId: number, reviewId: string) =>
  api
    .get(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation`, {
      headers: getRequestHeaders(),
    })
    .json<ReviewerQa_Evaluations>()

export const deleteReviewEvaluation = (
  connectionId: number,
  reviewId: string,
  evaluationId: string,
  assignmentId: string,
) =>
  api
    .delete(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}`, {
      searchParams: qs.stringify({ assignmentId }),
      headers: getRequestHeaders(),
    })
    .json()

export const markEvaluationViewed = (connectionId: number, reviewId: string, evaluationId: string) =>
  api
    .put(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}/viewed`, {
      headers: getRequestHeaders(),
    })
    .json()

export const updateReviewEvaluation = async (
  connectionId: number,
  reviewId: string,
  evaluationId: string,
  evaluationScore: ReviewerQa_EvaluationScoreRequest,
  comment: ReviewerQa_EvaluationCommentRequest,
): Promise<ReviewerQa_Evaluation> => {
  const response = await api
    .put(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}`, {
      json: { evaluationScore, comment },
      headers: getRequestHeaders(),
    })
    .json<ReviewerQa_Evaluation>()

  await uploadUnsavedImages(comment?.comment)

  return response
}

export const postEvaluationComment = async (payload: {
  connectionId: number
  reviewId: string
  evaluationId: string
  data: { comment: string }
}): Promise<ReviewerQa_EvaluationCommentResponse> => {
  const response = await api
    .post(
      `ticket/connection/${payload.connectionId}/reviews/${payload.reviewId}/evaluation/${payload.evaluationId}/comment`,
      {
        json: payload.data,
        headers: getRequestHeaders(),
      },
    )
    .json<ReviewerQa_EvaluationCommentResponse>()

  await uploadUnsavedImages(payload.data.comment)

  return response
}

export const updateEvaluationComment = async (
  payload: ReviewerQa_UpdateEvaluationCommentRequest,
): Promise<ReviewerQa_EvaluationCommentResponse> => {
  const response = await api
    .put(
      `ticket/connection/${payload.connectionId}/reviews/${payload.reviewId}/evaluation/${payload.evaluationId}/comment/${payload.commentId}`,
      {
        json: { comment: payload.comment },
        headers: getRequestHeaders(),
      },
    )
    .json<ReviewerQa_EvaluationCommentResponse>()

  await uploadUnsavedImages(payload.comment)

  return response
}

export const deleteEvaluationComment = ({
  connectionId,
  reviewId,
  evaluationId,
  commentId,
}: ReviewerQa_DeleteEvaluationCommentRequest) => {
  return api
    .delete(`ticket/connection/${connectionId}/reviews/${reviewId}/evaluation/${evaluationId}/comment/${commentId}`, {
      headers: getRequestHeaders(),
    })
    .json()
}

export const getWorkspaceScorecardsForReview = ({
  externalTicketId,
  connectionId,
}: AdvancedScorecard_GetReviewWorkspaceScorecardsRequest) =>
  api
    .extend({ hooks: { afterResponse: undefined } })
    .get('settings/reviews/workspaces/scorecards/review', {
      headers: getRequestHeaders(),
      searchParams: { externalTicketId, connectionId },
    })
    .json<AdvancedScorecard_GetWorkspaceScorecardsForReviewResponse>()

const getWorkspaceScorecardNonMemoized = ({
  scorecardId,
  externalTicketId,
  connectionId,
  conditional,
  onlyActive,
  signal,
  promptAnomalies,
}: AdvancedScorecard_GetWorkspaceScorecardRequest & { signal?: AbortSignal }) =>
  api
    .extend({ hooks: { afterResponse: undefined } })
    .get(`settings/reviews/workspaces/scorecards/${scorecardId}/review`, {
      headers: getRequestHeaders(),
      searchParams: { externalTicketId, connectionId, conditional, onlyActive, promptAnomalies },
      signal,
    })
    .json<AdvancedScorecard_WorkspaceScorecard>()

export const getWorkspaceScorecard = pMemoize(getWorkspaceScorecardNonMemoized, {
  cache: new ExpiryMap(1000 * 60 * 2),
  cacheKey: JSON.stringify,
})
